import {
  adjustShift,
  describeOverShort,
  minorToInput,
  ownerEntryDeltaMinor,
  parseMoneyToMinor,
  withinShift,
} from '@didto/domain';
import type {
  OwnerEntryKind,
  ShiftPaymentMissedPayload,
  ShiftPaymentView,
  ShiftRefundMissedPayload,
  UnpaidOrderView,
} from '@didto/domain';
import { Button, Checkbox, ErrorState, Field, Input, Notice, Select, Sheet } from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useOwnerReview } from '@/app/ownerReview';
import { useToast } from '@/app/toast';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { fetchPaymentMethods, paymentKeys } from '@/lib/payments';
import { supabase } from '@/lib/supabase';
import type { ShiftDetailView } from '@/lib/review';
import { useFormats } from './reviewItems';

/**
 * Money missed on a closed shift (Phase 7A-2B.1, ADR-028). The owner records, from their signed-in
 * account, a payment the customer made during the shift that nobody keyed, or money handed back
 * that nobody keyed. These become real ledger rows on the closed shift through the ledger's own
 * capture and refund; the sheet offers only what the server would accept and says what the entry
 * will do to the figures before it is recorded. Money only: nothing is sent, nothing comes back to stock.
 */

export type OwnerEntryTarget =
  { kind: 'payment_missed' } | { kind: 'refund_missed'; payment?: ShiftPaymentView };

/** The time an entry is stated at: the close by default, or a time within the shift. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function OwnerEntrySheet({
  detail,
  target,
  onClose,
}: {
  detail: ShiftDetailView;
  target: OwnerEntryTarget;
  onClose: () => void;
}) {
  const f = useFormats();
  const online = useOnline();
  const { run, ensureFresh } = useOwnerReview();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const kind: OwnerEntryKind = target.kind;
  const sh = detail.shift;
  const a = detail.adjusted;
  const [orderId, setOrderId] = useState('');
  const [paymentId, setPaymentId] = useState(
    target.kind === 'refund_missed' ? (target.payment?.id ?? '') : '',
  );
  const [methodId, setMethodId] = useState('');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [whenMode, setWhenMode] = useState<'close' | 'time'>('close');
  const [when, setWhen] = useState(sh.closed_at ? toLocalInput(sh.closed_at) : '');
  const [partial, setPartial] = useState(false);
  const [again, setAgain] = useState(false);
  const [reason, setReason] = useState('');
  const methods = useQuery({ queryKey: paymentKeys.methods, queryFn: fetchPaymentMethods });
  const unpaid: UnpaidOrderView[] = detail.unpaid_orders;
  const order = unpaid.find((o) => o.id === orderId) ?? null;
  const tenders = useMemo(
    () => detail.payments.filter((p) => p.reversible_minor > 0 && !p.voided),
    [detail.payments],
  );
  const tender = tenders.find((p) => p.id === paymentId) ?? null;
  const chosenMethod = methods.data?.find((m) => m.id === methodId) ?? null;
  const amountMinor = parseMoneyToMinor(amount);
  const occurredAt = whenMode === 'close' ? null : when ? new Date(when).toISOString() : null;
  const timeOk =
    whenMode === 'close' ||
    (occurredAt !== null &&
      withinShift({ opened_at: sh.opened_at, closed_at: sh.closed_at }, occurredAt));
  // the preview: what the entry does to the close's difference as corrected (D1)
  const methodKind =
    kind === 'payment_missed'
      ? (chosenMethod?.kind ?? null)
      : (chosenMethod?.kind ?? tender?.method.kind ?? null);
  const preview =
    amountMinor != null && amountMinor > 0 && methodKind && a.over_short_minor != null
      ? adjustShift(
          {
            openingCountMinor: a.opening_count_minor,
            expectedOpeningMinor: a.expected_opening_minor,
            countedMinor: a.counted_minor ?? 0,
            expectedMinor: a.expected_minor,
            retainedMinor: a.retained_minor ?? 0,
            removedMinor: a.removed_minor ?? 0,
            removedTo: a.removed_to,
          },
          [{ kind: 'expected', deltaMinor: ownerEntryDeltaMinor(kind, methodKind, amountMinor) }],
        ).overShortMinor
      : null;
  const valid =
    reason.trim().length > 0 &&
    amountMinor != null &&
    amountMinor > 0 &&
    timeOk &&
    (kind === 'payment_missed'
      ? !!order &&
        !!chosenMethod &&
        amountMinor <= order.remaining_minor &&
        (amountMinor === order.remaining_minor || partial) &&
        (!chosenMethod.requires_reference || reference.trim().length > 0)
      : !!tender && amountMinor <= tender.reversible_minor);

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await record();
    } catch (e) {
      // the order's version could not be read: nothing was recorded
      toast(errorText(e), 'danger');
    } finally {
      setBusy(false);
    }
  };
  const record = async () => {
    if (!(await ensureFresh())) return;
    const base = {
      shift_id: sh.id,
      reason: reason.trim(),
      ...(occurredAt ? { occurred_at: occurredAt } : {}),
    };
    let r: unknown;
    if (kind === 'payment_missed') {
      const payload: ShiftPaymentMissedPayload = {
        ...base,
        order_id: order!.id,
        version: order!.version,
        payment_method_id: chosenMethod!.id,
        amount_minor: amountMinor!,
        ...(chosenMethod!.requires_reference ? { reference: reference.trim() } : {}),
        ...(partial ? { partial: true } : {}),
        ...(again ? { again: true } : {}),
      };
      r = await run('shift.payment_missed', payload);
    } else {
      const payload: ShiftRefundMissedPayload = {
        ...base,
        order_id: tender!.order_id,
        version: await orderVersion(tender!.order_id),
        payment_id: tender!.id,
        amount_minor: amountMinor!,
        ...(chosenMethod ? { payment_method_id: chosenMethod.id } : {}),
        ...(again ? { again: true } : {}),
      };
      r = await run('shift.refund_missed', payload);
    }
    if (r) {
      toast(kind === 'payment_missed' ? 'Missed payment recorded' : 'Missed refund recorded');
      onClose();
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title={kind === 'payment_missed' ? 'Record a missed payment' : 'Record a missed refund'}
    >
      <div className="d-stack" data-testid="owner-entry-sheet" data-kind={kind}>
        <p className="d-muted">
          {kind === 'payment_missed'
            ? 'A payment the customer made during this shift that nobody keyed. It becomes a real payment on this shift, at the time you state; nothing is sent to the kitchen.'
            : 'Money handed back during this shift that nobody keyed. It becomes a real refund from the tender you name, at the time you state; nothing comes back to stock.'}
        </p>
        {methods.error ? (
          <ErrorState
            title="Could not load the payment methods"
            message={errorText(methods.error)}
            onRetry={() => void methods.refetch()}
          />
        ) : null}
        {kind === 'payment_missed' ? (
          <Field
            label="The order"
            help={
              unpaid.length === 0
                ? 'No order placed before the close still owes money'
                : 'Orders placed before the close that still owe money'
            }
          >
            {(id) => (
              <Select
                id={id}
                value={orderId}
                onChange={(e) => {
                  setOrderId(e.target.value);
                  const o = unpaid.find((x) => x.id === e.target.value);
                  if (o) setAmount(minorToInput(o.remaining_minor));
                }}
                data-testid="entry-order"
              >
                <option value="">Choose an order</option>
                {unpaid.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.order_number} · {f.money(o.remaining_minor)} owed
                    {o.customer_name ? ` · ${o.customer_name}` : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : (
          <Field
            label="The payment it came back from"
            help={
              tenders.length === 0
                ? 'No payment of this shift can still give money back'
                : "This shift's payments that can still give money back"
            }
          >
            {(id) => (
              <Select
                id={id}
                value={paymentId}
                onChange={(e) => setPaymentId(e.target.value)}
                data-testid="entry-tender"
              >
                <option value="">Choose a payment</option>
                {tenders.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.order_number} · {f.money(p.amount_minor)} · {p.method.name}
                    {p.reversible_minor !== p.amount_minor
                      ? ` · ${f.money(p.reversible_minor)} left`
                      : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field
          label={kind === 'payment_missed' ? 'How it was paid' : 'How it was given back'}
          help={
            kind === 'refund_missed'
              ? 'Leave as the payment’s own method unless it went back another way'
              : undefined
          }
        >
          {(id) => (
            <Select
              id={id}
              value={methodId}
              onChange={(e) => setMethodId(e.target.value)}
              data-testid="entry-method"
            >
              <option value="">
                {kind === 'payment_missed'
                  ? 'Choose a method'
                  : tender
                    ? `${tender.method.name} (as paid)`
                    : 'Choose a payment first'}
              </option>
              {(methods.data ?? [])
                .filter((m) => m.enabled && !m.archived_at && m.kind !== 'complimentary')
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        {kind === 'payment_missed' && chosenMethod?.requires_reference && (
          <Field label={`${chosenMethod.name} reference`} help="From the provider's record">
            {(id) => (
              <Input
                id={id}
                value={reference}
                maxLength={64}
                onChange={(e) => setReference(e.target.value)}
                data-testid="entry-reference"
              />
            )}
          </Field>
        )}
        <Field
          label="Amount"
          help={
            kind === 'payment_missed'
              ? order
                ? `${f.money(order.remaining_minor)} owed`
                : 'In pesos'
              : tender
                ? `Up to ${f.money(tender.reversible_minor)}`
                : 'In pesos'
          }
        >
          {(id) => (
            <Input
              id={id}
              inputMode="decimal"
              className="d-input--money"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="entry-amount"
            />
          )}
        </Field>
        {kind === 'payment_missed' &&
          order &&
          amountMinor != null &&
          amountMinor < order.remaining_minor && (
            <div className="rv-check">
              <Checkbox
                checked={partial}
                onChange={setPartial}
                testId="entry-partial"
                label={
                  <span className="rv-check__text">
                    <span className="d-strong">Only part of what is owed</span>
                    <span className="d-help">A deposit, by the ledger’s partial-payment rule</span>
                  </span>
                }
              />
            </div>
          )}
        <Field label="When" help="Within the shift; the close unless you say otherwise">
          {(id) => (
            <div className="d-stack d-stack--tight">
              <Select
                id={id}
                value={whenMode}
                onChange={(e) => setWhenMode(e.target.value as 'close' | 'time')}
                data-testid="entry-when-mode"
              >
                <option value="close">
                  At the close ({sh.closed_at ? f.at(sh.closed_at) : '—'})
                </option>
                <option value="time">At a time during the shift</option>
              </Select>
              {whenMode === 'time' && (
                <Input
                  type="datetime-local"
                  aria-label="The time during the shift"
                  value={when}
                  onChange={(e) => setWhen(e.target.value)}
                  data-testid="entry-when"
                />
              )}
              {!timeOk && (
                <p className="d-error" role="alert">
                  That time is outside the shift.
                </p>
              )}
            </div>
          )}
        </Field>
        <Field label="Why" help="Required">
          {(id) => (
            <Input
              id={id}
              value={reason}
              maxLength={300}
              onChange={(e) => setReason(e.target.value)}
              data-testid="entry-reason"
            />
          )}
        </Field>
        <div className="rv-check">
          <Checkbox
            checked={again}
            onChange={setAgain}
            testId="entry-again"
            label={
              <span className="rv-check__text">
                <span className="d-strong">
                  Record it even if the same entry is already on this shift
                </span>
                <span className="d-help">
                  {kind === 'payment_missed'
                    ? 'The same order, method and amount twice is refused unless you say so'
                    : 'The same tender, method and amount twice is refused unless you say so'}
                </span>
              </span>
            }
          />
        </div>
        {preview != null && (
          <Notice tone="info" testId="entry-preview">
            The close will read {describeOverShort(preview, f.money).toLowerCase()}
            {methodKind && methodKind !== 'cash' ? ' (a non-cash entry moves no cash)' : ''}.
          </Notice>
        )}
        <Button
          variant="primary"
          size="lg"
          block
          busy={busy}
          disabled={!online || !valid}
          onClick={() => void submit()}
          data-testid="entry-confirm"
        >
          {kind === 'payment_missed' ? 'Record the payment' : 'Record the refund'}
        </Button>
      </div>
    </Sheet>
  );
}

/** The order's current version: the refund names the tender, the ledger still checks the order it is on. */
async function orderVersion(orderId: string): Promise<number> {
  const { data, error } = await supabase
    .from('orders')
    .select('version')
    .eq('id', orderId)
    .single();
  if (error) throw error;
  return data.version;
}
